import React from 'react';
import { motion } from 'framer-motion';
import { Target, Rocket, Award, Users } from 'lucide-react';
import { Helmet } from "react-helmet"; // SEO ke liye

const About = () => {
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <div className="bg-white min-h-screen pt-8 pb-16">
      {/* --- SEO HELMET --- */}
      <Helmet>
        <title>About ZYGMA | Leading Global Digital Growth Agency</title>
        <meta name="description" content="Discover ZYGMA's story. We are a team of strategic architects providing worldwide SEO, GMB optimization, and web development to turn businesses into digital powerhouses." />
        <meta name="keywords" content="About ZYGMA, Global Digital Agency, SEO Experts Worldwide, ZYGMA Mission, Digital Growth Architects" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4">
        
        {/* --- MAIN HERO-STYLE SECTION --- */}
        <div className="relative min-h-[500px] rounded-[3.5rem] overflow-hidden shadow-2xl bg-slate-900 mb-12">
          
          {/* Background Elements */}
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=2070" 
              alt="About ZYGMA Team" 
              className="w-full h-full object-cover opacity-20" 
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-900 to-slate-900"></div>
          </div>

          {/* Content Area */}
          <div className="relative z-10 flex flex-col justify-center items-center h-full text-center px-8 py-20">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="max-w-3xl"
            >
              <span className="bg-blue-600/20 text-blue-400 border border-blue-500/30 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6 inline-block">
                Our Global Mission
              </span>
              
              <h1 className="text-5xl md:text-7xl font-black text-white leading-[1.1] mb-8">
                Building the Future of <br />
                <span className="text-blue-500">Digital Growth.</span>
              </h1>
              
              <p className="text-gray-300 text-lg md:text-xl mb-12 leading-relaxed">
                ZYGMA is more than an agency. We are a team of strategic architects dedicated to 
                transforming businesses worldwide into digital powerhouses.
              </p>
            </motion.div>
          </div>
        </div>

        {/* --- STATS SECTION --- */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
          {[
            { label: "Active Clients", num: "50+", icon: <Users size={20}/> },
            { label: "Projects Done", num: "120+", icon: <Award size={20}/> },
            { label: "ROI Average", num: "300%", icon: <TrendingUp size={20}/> },
            { label: "Expert Team", num: "12+", icon: <Users size={20}/> }
          ].map((stat, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              className="bg-slate-50 border border-slate-100 p-8 rounded-[2rem] text-center"
            >
              <div className="flex justify-center mb-2 text-blue-600 opacity-50">{stat.icon}</div>
              <h3 className="text-4xl font-black text-slate-900 mb-2">{stat.num}</h3>
              <p className="text-blue-600 text-xs font-bold uppercase tracking-widest">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* --- CORE VALUES --- */}
        <div className="grid lg:grid-cols-2 gap-8 items-stretch">
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-slate-900 rounded-[3rem] p-10 md:p-16 text-white flex flex-col justify-center"
          >
            <h2 className="text-4xl font-black mb-8 leading-tight">
              Our DNA is built on <br />
              <span className="text-blue-500 text-5xl italic">Innovation.</span>
            </h2>
            <div className="space-y-8">
              {[
                { icon: <Target className="text-blue-400" />, title: "Precision Targeting", desc: "Data-driven strategies to find your exact customers globally." },
                { icon: <Rocket className="text-blue-400" />, title: "Fast Execution", desc: "Turning ideas into live campaigns in record time." }
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6">
                  <div className="w-12 h-12 bg-blue-600/20 border border-blue-500/30 rounded-2xl flex items-center justify-center shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="font-bold text-xl mb-1">{item.title}</h4>
                    <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="bg-blue-600 rounded-[3rem] p-12 text-center text-white flex flex-col items-center justify-center shadow-xl shadow-blue-600/20"
          >
            <h2 className="text-3xl md:text-4xl font-black mb-6 italic">"Result Oriented Growth."</h2>
            <p className="text-blue-100 mb-10 text-lg leading-relaxed">
              We don't just provide services; we provide the roadmap to your success. Ready to dominate internationally?
            </p>
            <a 
              href="/contact" 
              className="bg-white text-blue-600 px-12 py-4 rounded-2xl font-black hover:scale-105 transition-transform shadow-lg"
            >
              Start Your Journey
            </a>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

const TrendingUp = ({size}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline><polyline points="17 6 23 6 23 12"></polyline></svg>
);

export default About;